import { presetItemKey, trimString } from "./preset-workspace-ui-model.js";
import {
  presetChangeDisplayName,
  presetChangeSignalKey,
  renderPresetChangeValue,
} from "./preset-manager-change-value.js";

function element(tagName, className = "", text = "") {
  const node = document.createElement(tagName);
  if (className) {
    node.className = className;
  }
  if (text) {
    node.textContent = text;
  }
  return node;
}

function setHidden(node, hidden) {
  if (node) {
    node.hidden = Boolean(hidden);
  }
}

export function renderPresetLibrary({
  activeIconTemplate,
  empty,
  list,
  model,
  pending,
  query,
  translate,
}) {
  if (!list?.replaceChildren) {
    return;
  }
  const normalizedQuery = trimString(query).toLowerCase();
  const visibleItems = model.items.filter((item) => (
    !normalizedQuery || `${item.name} ${item.id}`.toLowerCase().includes(normalizedQuery)
  ));
  const activeLabel = translate("common.status.active", "Active");
  const selectedLabel = translate("common.status.selected", "Selected");
  const activateLabel = translate("presets.action.set_active", "Activate preset");
  const detailsLabel = translate("presets.details.label", "Preset details");
  const fragment = document.createDocumentFragment();
  const grouped = model.localPresets.length > 0
    || model.localPresetSources?.kind === "unavailable";
  const groupLabels = {
    "built-in": translate("presets.status.built_in", "Built in"),
    account: translate("account.settings.group.account", "Account"),
    local: translate("account.browser.title", "This browser"),
  };
  let currentGroup = "";
  for (const item of visibleItems) {
    if (grouped && item.group !== currentGroup) {
      currentGroup = item.group;
      const heading = element(
        "li",
        "px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-base-content/55 first:pt-1",
        groupLabels[currentGroup],
      );
      heading.dataset.role = `preset-group-${currentGroup}`;
      fragment.append(heading);
    }
    const selected = item.key === model.selectedKey;
    const active = item.key === presetItemKey(model.basedOn?.kind, model.basedOn?.id);
    const li = element(
      "li",
      `list-row mb-1 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-box p-2 ${selected ? "bg-base-100 shadow-sm ring-1 ring-base-300" : "hover:bg-base-100/60"}`,
    );
    const detailsButton = element(
      "button",
      "grid min-h-10 min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2 rounded-field px-2 py-1 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
    );
    detailsButton.type = "button";
    detailsButton.dataset.action = "select-preset";
    detailsButton.dataset.itemKey = item.key;
    detailsButton.setAttribute("aria-pressed", selected ? "true" : "false");
    detailsButton.setAttribute("aria-label", `${detailsLabel}: ${item.name}`);
    if (active) {
      detailsButton.setAttribute("aria-current", "true");
    }
    const activeMarker = element("span", "grid size-5 shrink-0 place-items-center");
    activeMarker.dataset.role = "preset-active-marker";
    activeMarker.setAttribute("aria-hidden", "true");
    if (active) {
      activeMarker.setAttribute("data-fishy-tooltip", activeLabel);
      const icon = activeIconTemplate?.content?.cloneNode?.(true);
      if (icon) {
        activeMarker.append(icon);
      }
    }
    const copy = element("span", "grid min-w-0 gap-0.5");
    copy.append(
      element("span", "truncate font-medium", item.name),
      element(
        "span",
        "truncate text-xs text-base-content/55",
        selected ? selectedLabel : detailsLabel,
      ),
    );
    detailsButton.append(activeMarker, copy);

    const activateButton = element(
      "button",
      `btn btn-sm min-h-10 shrink-0 sm:min-h-8 ${active ? "btn-ghost" : "btn-outline"}`,
    );
    activateButton.type = "button";
    activateButton.dataset.action = "activate-preset";
    activateButton.dataset.itemKey = item.key;
    activateButton.disabled = Boolean(pending) || active;
    activateButton.setAttribute(
      "aria-label",
      active ? `${item.name}: ${activeLabel}` : `${activateLabel}: ${item.name}`,
    );
    activateButton.append(element("span", "text-xs", active ? activeLabel : activateLabel));
    li.append(detailsButton, activateButton);
    fragment.append(li);
  }
  if (model.localPresetSources?.kind === "unavailable") {
    if (currentGroup !== "local") {
      const heading = element(
        "li",
        "px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-base-content/55 first:pt-1",
        groupLabels.local,
      );
      heading.dataset.role = "preset-group-local";
      fragment.append(heading);
    }
    const localEmpty = element(
      "li",
      "px-4 py-2 text-xs text-base-content/60",
      translate(
        "presets.grid.local_empty_account",
        "No local presets found. While logged in, presets are saved under your account.",
      ),
    );
    localEmpty.dataset.role = "local-presets-empty";
    fragment.append(localEmpty);
  }
  list.replaceChildren(fragment);
  const noMatches = model.ready
    && visibleItems.length === 0
    && model.localPresetSources?.kind !== "unavailable";
  if (empty) {
    empty.textContent = model.items.length
      ? translate("presets.grid.no_matches", "No presets match this search.")
      : !model.savedReferencesReady
        ? translate("presets.state.unavailable", "Preset storage is currently unavailable.")
        : translate("presets.grid.empty", "No presets are available.");
  }
  setHidden(empty, !noMatches);
}

export function renderPresetChanges({ cards, empty, list, model, summaryElement, table, translate }) {
  if (!list?.replaceChildren) {
    return;
  }
  const fragment = document.createDocumentFragment();
  const cardFragment = document.createDocumentFragment();
  for (const change of model.selectedChanges) {
    const row = element("tr");
    const setting = element("td", "max-w-56 font-medium");
    const settingLabel = presetChangeDisplayName(change);
    const settingHint = presetChangeSignalKey(change);
    const settingTooltip = element("div", "text-left");
    if (settingHint) {
      settingTooltip.tabIndex = 0;
      settingTooltip.setAttribute("data-fishy-tooltip", settingHint);
      settingTooltip.setAttribute("aria-label", `${settingLabel}: ${settingHint}`);
    }
    settingTooltip.append(element("span", "break-words", settingLabel));
    setting.append(settingTooltip);
    const oldValue = element("td", "max-w-64 text-xs");
    oldValue.append(renderPresetChangeValue(change, "old"));
    const newValue = element("td", "max-w-64 text-xs");
    newValue.append(renderPresetChangeValue(change, "new"));
    row.append(setting, oldValue, newValue);
    fragment.append(row);

    const card = element("article", "card card-border bg-base-100 shadow-sm");
    const cardBody = element("div", "card-body gap-3 p-4");
    const cardTitle = element("h4", "card-title text-sm", settingLabel);
    if (settingHint) {
      cardTitle.setAttribute("data-fishy-tooltip", settingHint);
      cardTitle.setAttribute("aria-label", `${settingLabel}: ${settingHint}`);
      cardTitle.tabIndex = 0;
    }
    const values = element("dl", "grid grid-cols-2 gap-3 text-xs");
    const oldGroup = element("div", "grid min-w-0 gap-1");
    oldGroup.append(
      element(
        "dt",
        "text-[0.6875rem] font-semibold uppercase tracking-wide text-base-content/50",
        translate("presets.changes.old", "Old"),
      ),
    );
    const oldDescription = element("dd", "min-w-0");
    oldDescription.append(renderPresetChangeValue(change, "old"));
    oldGroup.append(oldDescription);
    const newGroup = element("div", "grid min-w-0 gap-1");
    newGroup.append(
      element(
        "dt",
        "text-[0.6875rem] font-semibold uppercase tracking-wide text-base-content/50",
        translate("presets.changes.new", "New"),
      ),
    );
    const newDescription = element("dd", "min-w-0");
    newDescription.append(renderPresetChangeValue(change, "new"));
    newGroup.append(newDescription);
    values.append(oldGroup, newGroup);
    cardBody.append(cardTitle, values);
    card.append(cardBody);
    cardFragment.append(card);
  }
  list.replaceChildren(fragment);
  cards?.replaceChildren?.(cardFragment);
  const hasChanges = model.selectedChanges.length > 0;
  const summary = model.selectedIsActive && hasChanges
    ? model.selectedChangesTruncated
      ? translate("presets.changes.summary_truncated", "Showing the first {$count} changes.", {
          count: model.selectedChanges.length,
        })
      : translate("presets.changes.summary", "{$count} changes compared with the saved preset.", {
          count: model.selectedChanges.length,
        })
    : "";
  if (summaryElement) {
    summaryElement.textContent = summary;
  }
  setHidden(summaryElement, !summary);
  setHidden(table, !hasChanges);
  setHidden(cards, !hasChanges);
  if (empty) {
    empty.textContent = model.selectedIsActive
      ? translate("presets.changes.empty", "No differences from this preset.")
      : translate("presets.changes.inactive", "Set this preset active to inspect its changes.");
  }
  setHidden(empty, hasChanges || !model.ready || !model.selectedItem);
}
